import {
  IpItem,
  CreateIpListRequest,
} from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import {
  PrimaryButton,
  SecondaryButton,
  DropdownField,
  Table,
  TableColumnConfig,
  Text,
  TextField,
  useControlledField,
} from '@weave/design-system';
import { useAlert } from '@weave/alert-system';
import React, { FormEvent, useState } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { hasAccess } from '../../helpers/authz';
import { getErrorMessage } from '../../redux/axios';
import { Page } from '../shared';
import {
  useCreateIpListItem,
  useDeleteIpItem,
  useGetIpList,
  useUpdateIpListItem,
} from '../../apis/system-security/queries';

const IpActionDropdown = ({
  item,
  disabled,
  onChange,
}: {
  item: IpItem;
  disabled: boolean;
  onChange: (action: string) => void;
}) => {
  const field = useControlledField({
    type: 'dropdown',
    value: item.action ?? '',
    onChange,
  });

  return (
    <DropdownField
      {...field}
      name={`ip-action-${item.id}`}
      label={`Action for ${item.cidr || item.ip}`}
      disabled={disabled || !item.id}
    >
      <DropdownField.Option value="allow">Allow</DropdownField.Option>
      <DropdownField.Option value="block">Block</DropdownField.Option>
    </DropdownField>
  );
};

const IpNotesField = ({
  item,
  disabled,
  onChange,
}: {
  item: IpItem;
  disabled: boolean;
  onChange: (notes: string) => void;
}) => {
  const field = useControlledField({
    type: 'text',
    value: item.notes ?? '',
    onChange,
  });
  return (
    <TextField
      {...field}
      name={`ip-notes-${item.id}`}
      label={`Notes for ${item.cidr || item.ip}`}
      disabled={disabled || !item.id}
    />
  );
};

export const ManageBannedIpsPage = () => {
  const [ipAddress, setIpAddress] = useState('');
  const [drafts, setDrafts] = useState<
    Record<string, { action?: string; notes?: string }>
  >({});
  const ipAddressField = useControlledField({
    type: 'text',
    value: ipAddress,
    onChange: setIpAddress,
  });
  const canManage = hasAccess(Permission.SYSTEM_SECURITY_MANAGE);
  const alerts = useAlert();
  const { data, isLoading, isError: isListError } = useGetIpList(canManage);
  const updateMutation = useUpdateIpListItem();
  const createMutation = useCreateIpListItem();
  const deleteMutation = useDeleteIpItem();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const ip = ipAddress.trim();
    if (!ip) {
      alerts.error('Enter an IP address or CIDR range');
      return;
    }

    const request: CreateIpListRequest = {
      ipItem: {
        id: crypto.randomUUID(),
        ...(ip.includes('/') ? { cidr: ip } : { ip }),
        action: 'block',
      },
    };
    createMutation.mutate(request, {
      onSuccess: () => {
        setIpAddress('');
        alerts.success('Banned IP added');
      },
      onError: (error) => {
        alerts.error(String(getErrorMessage(error) || 'Failed to add banned IP'));
      },
    });
  };

  const handleDelete = (item: IpItem) => {
    if (!item.id) {
      alerts.error('Cannot remove this item because it has no ID');
      return;
    }
    deleteMutation.mutate(item.id, {
      onSuccess: () => {
        alerts.success('Banned IP removed');
      },
      onError: (error) => {
        alerts.error(String(getErrorMessage(error) || 'Failed to remove banned IP'));
      },
    });
  };

  const ipList = (data?.ipList ?? []).filter(
    (item) => item.ip?.trim() || item.cidr?.trim()
  );
  const isMutating =
    createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading;

  const updateDraft = (item: IpItem, changes: { action?: string; notes?: string }) => {
    if (!item.id) {
      alerts.error('Cannot edit this item because it has no ID');
      return;
    }
    const id = item.id;
    setDrafts((current) => ({
      ...current,
      [id]: { ...current[id], ...changes },
    }));
  };

  const handleSave = (item: IpItem) => {
    if (!item.id) {
      alerts.error('Cannot save this item because it has no ID');
      return;
    }
    const id = item.id;
    updateMutation.mutate(
      { ipItem: { ...item, ...drafts[id] } },
      {
        onSuccess: () => {
          setDrafts((current) => {
            const next = { ...current };
            delete next[id];
            return next;
          });
          alerts.success('IP entry updated');
        },
        onError: (error) => {
          alerts.error(String(getErrorMessage(error) || 'Failed to update IP entry'));
        },
      }
    );
  };

  const columns: TableColumnConfig<IpItem>[] = [
    {
      Header: 'IP address / CIDR',
      id: 'address',
      accessor: (item) => item.cidr || item.ip || '',
    },
    {
      Header: 'Action',
      id: 'action',
      accessor: (item) => (
        <IpActionDropdown
          item={{ ...item, ...(item.id ? drafts[item.id] : undefined) }}
          disabled={isMutating}
          onChange={(action) => updateDraft(item, { action })}
        />
      ),
    },
    {
      Header: 'Notes',
      id: 'notes',
      accessor: (item) => (
        <IpNotesField
          item={{ ...item, ...(item.id ? drafts[item.id] : undefined) }}
          disabled={isMutating}
          onChange={(notes) => updateDraft(item, { notes })}
        />
      ),
    },
    {
      Header: 'Manage',
      id: 'manage',
      accessor: (item) => (
        <>
          <PrimaryButton
            type="button"
            disabled={
              !item.id ||
              isMutating ||
              !drafts[item.id] ||
              ((drafts[item.id].action ?? item.action ?? '') === (item.action ?? '') &&
                (drafts[item.id].notes ?? item.notes ?? '') === (item.notes ?? ''))
            }
            onClick={() => handleSave(item)}
            style={{ width: 'auto', marginRight: 8 }}
          >
            Save
          </PrimaryButton>
          <SecondaryButton
            type="button"
            disabled={!item.id || isMutating}
            onClick={() => handleDelete(item)}
          >
            Remove
          </SecondaryButton>
          {!item.id && <Text role="alert">Cannot modify this item: missing ID.</Text>}
        </>
      ),
    },
  ];

  return (
    <Page title="Manage Banned IPs">
      {!canManage ? (
        <Text>You do not have permission to manage banned IPs.</Text>
      ) : (
        <>
          <form onSubmit={handleSubmit}>
            <TextField
              {...ipAddressField}
              name="ipAddress"
              label="IP address or CIDR range"
            />
            <PrimaryButton
              type="submit"
              disabled={!ipAddress.trim() || isMutating}
              style={{ width: 'auto', marginTop: 16 }}
            >
              {createMutation.isLoading ? 'Adding...' : 'Add IP'}
            </PrimaryButton>
          </form>

          <section aria-label="IP list" style={{ marginTop: 24 }}>
            <Text as="h2">IP List</Text>
            {isLoading && <Text>Loading banned IPs...</Text>}
            {isListError && <Text role="alert">Unable to load the banned IP list.</Text>}
            {!isLoading && !isListError && ipList.length === 0 && (
              <Text>No IP entries found.</Text>
            )}
            {!isLoading && !isListError && ipList.length > 0 && (
              <Table colConfig={columns} data={ipList} />
            )}
          </section>
        </>
      )}
    </Page>
  );
};
